<script>
    import { onMount } from "svelte";

    import { fromNow } from "$/utils/date";
    import { getFullName, getEffectivePricingPlan } from "$/utils/Utils";

    import { AppStore } from "$/stores/AppStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { TokenStore } from "$/stores/TokenStore";

    import { AnalyticsService } from "$/services/AnalyticsService";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import { EventService } from "$/services/EventService";

    import { TenantService } from "$/services/TenantService";
    import { LoginService } from "$/services/LoginService";
    import { ClusterStore } from "$/stores/ClusterStore";

    export let plan;
    export let isLoading = true;
    export let expirationDate = null;
    export let status = null;
    export let isAnnual = false;

    // computed properties
    $: effectiveActivePlan = getEffectivePricingPlan($TenantStore?.active);
    $: activePlan = $TenantStore?.active?.pricingPlan;

    $: isProcessingPayment = false;
    $: isProPlan = plan === 1;
    $: isTeamPlan = plan === 2;

    $: isActive = effectiveActivePlan === plan;
    $: isIncluded = effectiveActivePlan > plan;
    $: isUnavailable = plan < 2 && activePlan === 2;
    $: isExpired = $TenantStore && TenantStore.isExpired();

    $: expirationDateIn2Weeks =
        expirationDate &&
        expirationDate - Date.now() < 1209600000 &&
        expirationDate > Date.now();
    $: isTrial = status === "on_trial";
    $: isCancelled = status === "cancelled";
    $: isPaused = status === "paused";
    $: isUpgradable =
        effectiveActivePlan < plan ||
        (expirationDateIn2Weeks && isActive && !isTrial);

    $: isOwner = $TenantStore && TenantStore.isOwner($TokenStore?.user?.id);

    $: productId = isProPlan
        ? isAnnual
            ? ServiceConsts.PADDLE_PRODUCT_IDS.PRO_ANNUAL
            : ServiceConsts.PADDLE_PRODUCT_IDS.PRO_MONTHLY
        : isAnnual
          ? ServiceConsts.PADDLE_PRODUCT_IDS.TEAM_ANNUAL
          : ServiceConsts.PADDLE_PRODUCT_IDS.TEAM_MONTHLY;

    async function captureAnalyticsEvent(eventName) {
        try {
            AnalyticsService.trackEvent(
                $TokenStore?.user?.id,
                $TokenStore?.user?.email,
                eventName,
            );
           await EventService.trackEvent(eventName);
        } catch (error) {
            console.error("CW Analytics failed to capture event", error);
        }
    }

    function setupPaddle() {

        // check Paddle is loaded
        if (!window.Paddle) return;

        try {
            // Set environment to sandbox for testing
            if(ServiceConsts.isDevMode()) window.Paddle.Environment.set('sandbox');

            // Initialize Paddle with client token
            window.Paddle.Initialize({
                token: ServiceConsts.PADDLE_CLIENT_TOKEN,
                eventCallback: (event) => {

                    if (event.name == "checkout.loaded") {                    
                        captureAnalyticsEvent("checkout-view-cart");
                        dataLayer.push({
                            event: "checkout-view-cart",
                            eventCategory: "Checkout",
                            eventAction: "View Cart",
                            eventLabel: "View Cart",
                            eventValue: 1,
                        });
                    }

                    if (event.name == "checkout.closed") {
                        console.log("Checkout.closed", event);
                    }

                    if(event.name == "checkout.error") {
                        console.log("Checkout.error", event);
                    }

                    if (event.name === "checkout.completed") {
                        captureAnalyticsEvent("checkout-success");
                        dataLayer.push({
                            event: "checkout-success",
                            eventCategory: "Checkout",
                            eventAction: "Success",
                            eventLabel: "Success",
                            eventValue: 1,
                        });

                        console.log("Checkout.completed", event);

                        /**
                         * NOTE: Paddle is initialized only once per page, so accessing component properties
                         *       is not possible. We need to use the event data to determine the plan.
                         */
                        let isProPlan = event.data.items[0].price_id === ServiceConsts.PADDLE_PRODUCT_IDS.PRO_MONTHLY || 
                                      event.data.items[0].price_id === ServiceConsts.PADDLE_PRODUCT_IDS.PRO_ANNUAL;
                        let isTeamPlan = event.data.items[0].price_id === ServiceConsts.PADDLE_PRODUCT_IDS.TEAM_MONTHLY || 
                                       event.data.items[0].price_id === ServiceConsts.PADDLE_PRODUCT_IDS.TEAM_ANNUAL;

                        if(isProPlan) {

                            console.log("isProPlan", isProPlan);

                            // optimistic update
                            TenantStore.updatePricingPlanLocally(plan);

                            // close the checkout
                            Paddle.Checkout.close();
                        }

                        if(isTeamPlan) {

                            console.log("isTeamPlan", isTeamPlan);

                            let currentTenantCount = $TenantStore.tenants.length;

                            // pool until the tenant count has increases
                            let poolingInterval = setInterval(async () => {

                                // NOTE: We're using a service not to trigger all API calls that
                                //       come from init() or load() functions
                                let tenants = await TenantService.getTenants();

                                if(tenants.length > currentTenantCount) {

                                    // clear interval to stop polling
                                    clearInterval(poolingInterval);

                                    // find the latest tenant
                                    let latestTenant = tenants.sort((a, b) => a.createdOn - b.createdOn)[tenants.length - 1];
                                    
                                    // refresh token and load the latest tenant
                                    const refreshedToken = await LoginService.refreshTokenLogin();
                                    await TokenStore.updateTokenStore(refreshedToken);
                                    await TenantStore.load(latestTenant.id);
                                    await ClusterStore.init();

                                    // close the checkout
                                    Paddle.Checkout.close();
                                }
                            }, 3000);
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Paddle initialization error:', error);
        }
    }

    // on "/payment" page load Paddle scripts defer loading so we need to wait for them to load
    // before we can setup Paddle
    $: $AppStore.paddle.isLoaded ? setupPaddle() : null;

    // if Paddle scripts are already loaded (user has landed on anything other than "/payment")
    // we can setup Paddle right away
    onMount(async () => {
        if ($AppStore.paddle.isLoaded) setupPaddle();
    });

    function openCheckout() {
        if (!window.Paddle) {
            console.error('Paddle is not loaded');
            return;
        }

        try {
            const items = [{
                priceId: productId,
                quantity: isProPlan ? 1 : 3
            }];

            const customer = {
                email: $TokenStore?.user?.username ?? "",
                name: getFullName($TokenStore?.user) ?? ""
            };

            window.Paddle.Checkout.open({
                items,
                customer,
                discountCode: isProPlan ? undefined : isAnnual ? 'TEAM300' : 'TEAM30',
                allowDiscountRemoval: false,
                settings: {
                    displayMode: 'overlay',
                    theme: 'light',
                    locale: 'en',
                    frameTarget: 'paddle-checkout-container',
                    variant: 'one-page'
                },
                customData: {
                    userId: $TokenStore?.user?.id ?? "",
                    tenantId: $TenantStore?.active?.id ?? ""
                }
            });
        } catch (error) {
            console.error('Paddle checkout error:', error);
        }
    }
</script>

{#if isExpired && isTeamPlan}
    <a
        class="pricing-plan-button pricing-plan-button--upgradable"
        class:pricing-plan-button--active={!isOwner}
        class:pricing-plan-button--disabled={!isOwner}
        href={`${ServiceConsts.SERVER_URL}/billing/${$TenantStore?.active?.id}/portal`}
        target="_blank"
        rel="noreferrer"
    >
        {isOwner ? "Renew" : "Expired"}
    </a>
{:else}
    <div id="paddle-checkout-container" class="checkout-container">
        <button
            class="pricing-plan-button"
            class:pricing-plan-button--upgradable={isUpgradable}
            class:pricing-plan-button--active={isActive}
            class:pricing-plan-button--included={isIncluded ||
                isLoading ||
                isUnavailable}
            class:pricing-plan-button--disabled={!isUpgradable ||
                isLoading ||
                isUnavailable}
            on:click={openCheckout}
        >
            {isProcessingPayment
                ? "Upgrading..."
                : isLoading
                  ? "Loading..."
                  : isIncluded
                    ? "Included"
                    : isUnavailable
                      ? "Unavailable"
                      : isCancelled
                        ? "Cancelled"
                        : isPaused
                          ? "Paused"
                            : isTrial
                                ? `${expirationDate < Date.now() ? "Trial ended" : "Trial ends"} ${fromNow(expirationDate)}`
                                : expirationDateIn2Weeks
                                ? `Expires ${fromNow(expirationDate)}`
                                : isActive
                                    ? "Active"
                                    : isUpgradable
                                    ? isTeamPlan
                                        ? "Start Free Trial"
                                        : "Upgrade"
                                    : ""}
        </button>
    </div>
{/if}

<style>
    .pricing-plan-button {
        @apply block w-full cursor-pointer select-none self-center rounded-full bg-pink-500 px-4 py-2 text-center text-sm font-medium text-white hover:bg-pink-600;
    }
    .pricing-plan-button--disabled {
        @apply pointer-events-none;
    }
    .pricing-plan-button--active {
        @apply cursor-default bg-gray-800 hover:bg-gray-900;
    }
    .pricing-plan-button--included {
        @apply cursor-default bg-gray-400 hover:bg-gray-500;
    }
    .pricing-plan-button--upgradable {
        @apply cursor-pointer;
    }
    .checkout-container {
        @apply w-full;
    }
</style> 